import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * Header actions component that manages cart notifications.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];

  /** @type {MutationObserver | undefined} */
  #ringWidthObserver;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);

    const shopifyAccount = this.querySelector('shopify-account');
    if (shopifyAccount) this.#patchShopifyAccountRingWidth(shopifyAccount);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
    this.#ringWidthObserver?.disconnect();
  }

  /**
   * shopify-account hardcodes --ring-width directly on .account-button--shop-customer
   * inside its shadow root, so it can't be overridden from outside with a CSS variable.
   * Injects a stylesheet directly into the (open) shadow root instead, and re-applies it
   * if the component re-renders its shadow content (e.g. on sign-in/sign-out).
   * @param {Element} shopifyAccount
   * @param {number} [attempts]
   */
  #patchShopifyAccountRingWidth(shopifyAccount, attempts = 0) {
    const root = shopifyAccount.shadowRoot;

    if (!root) {
      if (attempts > 50) return;
      requestAnimationFrame(() => this.#patchShopifyAccountRingWidth(shopifyAccount, attempts + 1));
      return;
    }

    const ensureStyle = () => {
      if (root.querySelector('style[data-ring-width-fix]')) return;
      const style = document.createElement('style');
      style.setAttribute('data-ring-width-fix', '');
      style.textContent = '.account-button--shop-customer { --ring-width: 0 !important; }';
      root.appendChild(style);
    };

    ensureStyle();
    this.#ringWidthObserver = new MutationObserver(ensureStyle);
    this.#ringWidthObserver.observe(root, { childList: true, subtree: true });
  }

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #onCartUpdate = (event) => {
    const cartCount = event.detail.resource?.item_count;
    if (cartCount === undefined) return;

    this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
